import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';

const BoolBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $val }) => $val ? '#2e7d3215' : '#9e9e9e15'};
  color: ${({ $val }) => $val ? '#2e7d32' : '#9e9e9e'};
`;

// Köken rozeti: şablondan kopyalanmış politikalarda görünür. Şablon
// soft-delete edilmişse backend sourceTemplateName'i null döner ve rozet
// otomatik olarak kaybolur (SourceTemplateId tarihsel kayıt olarak kalsa da).
const SourceBadge = styled.span`
  font-size: 11px;
  color: #64748b;
  margin-left: 8px;
`;

// "Yeni" çekmecesinin gövdesi. Şablon seçiliyse (AKM POC §6.4) POST /v1/leave-policies/from-template/{id}
// (LeavePolicyFromTemplateRequest: name — boşsa şablonun adı — ve isDefault); değilse düz oluşturma.
export const toLeavePolicyRequest = (formData) => ({
  name: (formData?.name || '').trim(),
  isDefault: !!formData?.isDefault,
});

export const useLeavePolicyApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [templates, setTemplates] = useState([]);
  const lastValidQueryParams = useRef(null);

  // İzin politikası şablonları (ülke/çerçeve kalıpları; ör. "Kamu (657)"). api.js GET'te reddetmez —
  // sinyal res.data.status; okunamazsa seçici boş kalır, düz oluşturma çalışır.
  useEffect(() => {
    api.get('/v1/leave-policy-templates').then((res) => {
      if (res?.data?.status !== true) return;
      const result = res.data.data;
      const items = Array.isArray(result) ? result : (result?.data || []);
      setTemplates(items.map((tpl) => ({ id: tpl.id, name: tpl.name })));
    });
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/leave-policies?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      const body = toLeavePolicyRequest(formData);
      if (formData?.templateId) {
        await api.post(`/v1/leave-policies/from-template/${formData.templateId}`, { ...body, name: body.name || null });
      } else {
        await api.post('/v1/leave-policies', body);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/leave-policies/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/leave-policies/${ids[0]}`);
      } else {
        await api.post('/v1/leave-policies/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name', label: t('leavePolicy.name'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return (
            <>
              {v}
              {row?.sourceTemplateName ? (
                <SourceBadge>{t('leavePolicy.fromTemplate', { name: row.sourceTemplateName })}</SourceBadge>
              ) : null}
            </>
          );
        },
      },
    },
    {
      name: 'isDefault', label: t('leavePolicy.isDefault'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['true', 'false'],
          renderValue: (v) => v === 'true' ? t('common.yes') : t('common.no'),
        },
        customFilterListOptions: { render: (v) => v === 'true' ? t('common.yes') : t('common.no') },
        customBodyRender: (v) => <BoolBadge $val={v}>{v ? t('common.yes') : t('common.no')}</BoolBadge>,
      },
    },
    { name: 'variationCount', label: t('leavePolicy.variationCount'), options: { filter: false, sort: false } },
  ], [t, data]);

  // Şablon seçici yalnız YENİ politikada (düzenlemede satırın id'si vardır). Şablon seçiliyse ad
  // isteğe bağlıdır (boşsa şablonun adı alınır).
  const objectSchema = useMemo(() => [
    {
      name: 'templateId', label: t('leavePolicy.createFromTemplate'), type: 'select', grid: 12,
      options: templates, idKey: 'id', labelKey: 'name',
      description: t('leavePolicy.templateHint'),
      hidden: (fd) => !!fd?.id,
    },
    {
      name: 'name', label: t('leavePolicy.name'), type: 'text', grid: 12,
      required: (fd) => !fd?.templateId,
    },
    { name: 'isDefault', label: t('leavePolicy.isDefault'), type: 'checkbox', grid: 12 },
  ], [t, templates]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};
